import { v4 as uuidv4 } from 'uuid';
import { TimelineWriteOnlyContext } from '../../context-provider';
import {
  TransitionItem,
  createTransitionItem,
  DEFAULT_TRANSITION_DURATION_IN_FRAMES,
} from '../../items/transition/transition-item-type';
import {
  TransitionType,
  TransitionDirection,
} from '../../editor-property-panel/ui/contents/transition/transition-drag-data';
import { TransitionEdge } from '../../timeline/utils/transition-edge-utils';
import { EditorStarterItem } from '../../items/item-type';
import { EditorStarterAsset } from '../../assets/assets';
import {
  calculateTransitionExtension,
  calculateTransitionFrom,
} from '../../items/transition/transition-extension-utils';

// 기존 import 경로 호환성을 위한 re-export
export {
  getSourceExtendableFrames,
  getTargetExtendableFrames,
  calculateTargetExtensionInfo,
} from '../../items/transition/transition-extension-utils';

interface AddTransitionParams {
  timelineWriteContext: TimelineWriteOnlyContext;
  transitionType: TransitionType;
  direction?: TransitionDirection;
  edge: TransitionEdge;
  durationInFrames?: number;
  // frozen frames 계산에 필요한 추가 정보
  sourceItem: EditorStarterItem;
  targetItem: EditorStarterItem;
  assets: Record<string, EditorStarterAsset>;
  fps: number;
}

// 트랜지션이 이미 해당 경계에 존재하는지 확인
export const hasTransitionAtEdge = (
  transitions: Record<string, TransitionItem>,
  sourceItemId: string,
  targetItemId: string,
): boolean => {
  return Object.values(transitions).some(
    (t) => t.sourceItemId === sourceItemId && t.targetItemId === targetItemId,
  );
};

// 트랜지션 추가 액션
export const addTransition = ({
  timelineWriteContext,
  transitionType,
  direction,
  edge,
  durationInFrames = DEFAULT_TRANSITION_DURATION_IN_FRAMES,
  sourceItem,
  targetItem,
  assets,
  fps,
}: AddTransitionParams): TransitionItem | null => {
  const transitionId = uuidv4();

  // 트랜지션 from 위치 계산
  const from = calculateTransitionFrom(edge.edgeFrame, durationInFrames);

  // 확장 정보 및 frozen frames 통합 계산
  const {
    sourceExtendedFrames,
    targetExtendedFrames,
    targetExtendedAssetStartDelta,
    sourceFrozenFrames,
    targetFrozenFrames,
  } = calculateTransitionExtension({
    sourceItem,
    targetItem,
    assets,
    fps,
    transitionFrom: from,
    transitionDuration: durationInFrames,
  });

  const newTransition = createTransitionItem({
    id: transitionId,
    transitionType,
    direction,
    durationInFrames,
    sourceItemId: edge.sourceItemId,
    targetItemId: edge.targetItemId,
    trackId: edge.trackId,
    from,
    sourceFrozenFrames,
    targetFrozenFrames,
    // 논리적 확장 정보 저장 (UI에 반영되지 않음)
    sourceExtendedFrames,
    targetExtendedFrames,
    targetExtendedAssetStartDelta,
  });

  timelineWriteContext.setState({
    update: (state) => {
      // 이미 해당 경계에 트랜지션이 있는지 확인
      if (
        hasTransitionAtEdge(
          state.undoableState.transitions,
          edge.sourceItemId,
          edge.targetItemId,
        )
      ) {
        return state;
      }

      // 아이템은 수정하지 않음 - 확장 정보는 트랜지션에만 저장
      return {
        ...state,
        undoableState: {
          ...state.undoableState,
          transitions: {
            ...state.undoableState.transitions,
            [transitionId]: newTransition,
          },
        },
        selectedItems: [transitionId], // 새로 추가된 트랜지션 선택
      };
    },
    commitToUndoStack: true,
  });

  return newTransition;
};
